import { noop } from '@vueuse/core';
import routes from 'virtual:generated-pages';
import {
  globalMiddleware,
  namedMiddleware,
} from 'virtual:vite-auto-middleware';
import { createRouter, createWebHistory } from 'vue-router';

import AtomicLink from '@/components/atomics/AtomicLink.vue';
import { defineAppPlugin, useApp } from '@/main';
import type { RouteMiddleware } from '@/main';
import isFunction from '@/utils/isFunction';
import isString from '@/utils/isString';

import type { RouteLocationRaw, Router } from 'vue-router';

export interface NavigateToOptions {
  replace?: boolean;
}

// 參考
export async function preloadRouteComponents(
  to: RouteLocationRaw,
  router: Router & {
    _routePreloaded?: Set<string>;
    _preloadPromises?: Array<Promise<any>>;
  } = useApp().$router,
  route?: ReturnType<typeof router.resolve>
): Promise<void> {
  route = route || router.resolve(to);

  if (!router._routePreloaded) router._routePreloaded = new Set();
  if (router._routePreloaded.has(route.path)) return;

  const promises = (router._preloadPromises = router._preloadPromises || []);

  if (promises.length > 4) {
    // Defer adding new preload requests until the existing ones have resolved
    return Promise.all(promises).then(() => preloadRouteComponents(to, router));
  }

  router._routePreloaded.add(route.path);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(
      '%c☡ Preloaded',
      'display: inline-block; border: 1px solid #58A6FF66; color: #58A6FF; border-radius: 4px; padding: 2px 6px',
      `${route.path}`
    );
  }

  const components = route.matched
    .map(component => component.components?.default)
    .filter(component => isFunction(component));

  for (const component of components) {
    const promise = Promise.resolve((component as Function)())
      .catch(noop)
      .finally(() => promises.splice(promises.indexOf(promise)));

    promises.push(promise);
  }

  await Promise.all(promises);
}

export default defineAppPlugin(async context => {
  const router = createRouter({
    history: createWebHistory(),
    routes,
  });

  context.app.use(router);
  context.app.component('AtomicLink', AtomicLink);

  await router.isReady();

  router.beforeEach(async (to, from) => {
    type MiddlewareDef = string | RouteMiddleware;
    const middlewareEntries = new Set<MiddlewareDef>(
      globalMiddleware.filter(m => isFunction(m))
    );

    for (const component of to.matched) {
      const componentMiddleware = component.meta.middleware as
        | MiddlewareDef
        | MiddlewareDef[];

      if (!componentMiddleware) continue;
      if (Array.isArray(componentMiddleware)) {
        for (const entry of componentMiddleware) {
          middlewareEntries.add(entry);
        }
      } else {
        middlewareEntries.add(componentMiddleware);
      }
    }

    for (const entry of middlewareEntries) {
      const middleware = isString(entry)
        ? await namedMiddleware[entry]?.().then(r => r.default || r)
        : entry;

      if (!middleware) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`Middleware not found: ${entry}`);
        }
        continue;
      }

      const result = await context.app.runWithContext(() =>
        middleware(to, from)
      );

      if (result === true) continue;
      if (result || result === false) {
        return result;
      }
    }
  });

  context.hooks.hookOnce('app:created', async () => {
    await router.replace({
      ...router.currentRoute.value,
      force: true,
    });
  });

  return {
    provide: {
      router,
    },
  };
});

declare module '@vue/runtime-core' {
  export interface GlobalComponents {
    AtomicLink: typeof AtomicLink;
  }
}

declare module 'vue-router' {
  interface BreadcrumbItem {
    title: string;
  }

  interface RouteMeta {
    /**
     * 頁面靜態標題
     */
    title?: string;

    /**
     * 頁面靜態 breadcrumb
     */
    breadcrumb?: BreadcrumbItem[];
  }
}

declare module 'src/main' {
  interface Context {
    /**
     * Router instance.
     */
    $router: Router;
  }
}

export {};
